import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
  useService,
} from '../../../../../hooks';
import MedicalResultsService, {
  Result,
  ResultHistory,
} from '../../../../../services/MedicalResultsService';
import { MeasurementDimension } from '../../Measurements/types';
import { ResponseStats } from '../../Measurements/useQueriedData';
import { medicalResultsMeasurementsMapper } from '../../utils/dynamicDataMappers/medical-results/measurements-mapper';
import { LabResultsConfigItem } from '../useLabResultsConfig';
import { shouldIncludeDocumentExtractionV2 } from '../interlacedExtractedResults';

const SERVICE = new MedicalResultsService();
const INCLUDE_CLINIC_ONLY = true;
const PAGE_SIZE = 200;
const RESULT_COUNT = '2';

export type UseFetchDimensionDataArgs = {
  patientId: string;
  labResultsConfigItem?: LabResultsConfigItem;
};

export type DimensionDatum = {
  id: string;
  dimension: MeasurementDimension;
  responseStats: ResponseStats;
  systems: fhir.Coding[];
  resultSources?: ResultHistory['source'][];
};

export function useFetchDimensionData({
  patientId,
  labResultsConfigItem,
}: UseFetchDimensionDataArgs) {
  const [getResultsV3] = useService(SERVICE.getResultsV3);
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const getFeatureFlag = useFeatures();
  const shouldInterlaceExtractedResults = shouldIncludeDocumentExtractionV2({
    source: labResultsConfigItem?.source,
    isDocumentExtractionV2Enabled: getFeatureFlag('documentExtractionV2Ui'),
  });

  const select = useCallback(
    (results: Result[]) => {
      const transformedData = medicalResultsMeasurementsMapper(
        results,
        labResultsConfigItem?.source,
      );
      const dimensions = transformedData.dimensions.map((dimension, index) => ({
        id: dimension.id,
        dimension,
        responseStats: transformedData.responses[index],
        systems: results[index].systems,
        resultSources: results[index].results?.map(result => result.source),
      }));
      const coding = labResultsConfigItem?.coding;
      if (coding) {
        const results = dimensions.filter(result =>
          result.systems.some(
            system =>
              system.code === coding.code && system.system === coding.system,
          ),
        );
        return results;
      }
      return dimensions;
    },
    [labResultsConfigItem],
  );

  return useQuery({
    queryKey: [
      'medical-results',
      'v3',
      patientId,
      'results',
      {
        projectId,
        cohortId,
        shouldInterlaceExtractedResults,
      },
      labResultsConfigItem?.source,
    ],
    queryFn: async () => {
      const data = [];
      let nextPageToken = '';

      do {
        const response = await getResultsV3({
          patientId,
          project: projectId,
          cohort: cohortId,
          includeClinicOnly: INCLUDE_CLINIC_ONLY,
          pageSize: PAGE_SIZE,
          resultCount: RESULT_COUNT,
          nextPageToken,
          source: labResultsConfigItem?.source,
          includeDocumentExtractionV2: shouldInterlaceExtractedResults,
        });

        data.push(...response.data.items);

        const searchParams = new URLSearchParams(
          typeof response.data.links.next === 'string'
            ? response.data.links.next?.split('?')[1]
            : '',
        );
        nextPageToken = searchParams.get('nextPageToken') || '';
      } while (nextPageToken);

      return data;
    },
    select,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
